import { useEffect, useRef } from 'react';

/**
 * Touch gestures of the calendar (1.24, Mike 18.10.): one pointer, locked to the axis it starts
 * in, so scrolling up and down never turns a page by accident. Mouse users get buttons instead –
 * every gesture has one (`.claude/rules/ui.md`: swipe only in addition).
 *
 * - `onSwipe(dir)`: a horizontal swipe of at least 60 px (1 = next, -1 = previous). While the
 *   finger moves, `onDrag(dx)` lets the page follow it.
 * - `onPull(dy, done)`: vertical drags (the month folding), reported live and at the end.
 * - `scrollX`: sideways-scrolling content scrolls first; pulling past its first or last day moves
 *   the page and turns it (the week by person – no accidental turns while scrolling).
 *
 * The outer 16 px stay free for the system's back gesture.
 */
export interface SwipeOptions {
  onSwipe?: (dir: 1 | -1, el: HTMLElement) => void;
  onDrag?: (dx: number, el: HTMLElement) => void;
  onPull?: (dy: number, done: boolean) => void;
  /** Scroll this element sideways first; only pulling past its edge turns the page. */
  scrollX?: boolean;
  disabled?: boolean;
  /**
   * Keep the element on its own compositor layer for good. Creating and dropping the layer on
   * every swipe made Android repaint the app's header and tab bar – they flickered (Mike, 19.10.:
   * "dann springt und flackert das ganze Menü oben und unten mit").
   */
  layer?: boolean;
}

const EDGE = 16;
const SLOP = 9;

export function useSwipe<T extends HTMLElement>(options: SwipeOptions) {
  const ref = useRef<T>(null);
  const opts = useRef(options);
  opts.current = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (opts.current.layer) el.style.willChange = 'transform, opacity';
    let g: {
      id: number;
      x0: number;
      y0: number;
      axis?: 'x' | 'y' | 'none';
      sl0: number;
    } | null = null;

    const down = (e: PointerEvent) => {
      if (opts.current.disabled || e.pointerType === 'mouse' || g) return;
      if (e.clientX < EDGE || e.clientX > window.innerWidth - EDGE) return;
      g = { id: e.pointerId, x0: e.clientX, y0: e.clientY, sl0: el.scrollLeft };
    };
    const move = (e: PointerEvent) => {
      if (!g || e.pointerId !== g.id) return;
      const dx = e.clientX - g.x0;
      const dy = e.clientY - g.y0;
      if (!g.axis) {
        if (Math.hypot(dx, dy) < SLOP) return;
        const horizontal = Math.abs(dx) > Math.abs(dy) * 1.2;
        g.axis = horizontal
          ? opts.current.onSwipe
            ? 'x'
            : 'none'
          : Math.abs(dy) > Math.abs(dx) && opts.current.onPull
            ? 'y'
            : 'none';
      }
      if (g.axis === 'x') opts.current.onDrag?.(pull(g, dx), el);
      else if (g.axis === 'y') opts.current.onPull?.(dy, false);
    };
    /**
     * How far the page itself moves. Sideways-scrolling content (the week by person) scrolls
     * first; only what is pulled beyond its first or last day moves the page – so scrolling never
     * turns the week by accident (Mike, 19.10.).
     */
    const pull = (s: NonNullable<typeof g>, dx: number) => {
      if (!opts.current.scrollX) return dx;
      const max = Math.max(0, el.scrollWidth - el.clientWidth);
      const target = s.sl0 - dx;
      el.scrollLeft = Math.min(max, Math.max(0, target));
      return target < 0 ? -target : target > max ? max - target : 0;
    };
    const up = (e: PointerEvent, cancelled = false) => {
      if (!g || e.pointerId !== g.id) return;
      const dx = e.clientX - g.x0;
      const dy = e.clientY - g.y0;
      const { axis } = g;
      const moved = axis === 'x' ? pull(g, dx) : 0;
      g = null;
      if (axis === 'y') opts.current.onPull?.(cancelled ? 0 : dy, true);
      if (axis !== 'x') return;
      opts.current.onDrag?.(0, el);
      if (!cancelled && Math.abs(moved) > 60) opts.current.onSwipe?.(moved < 0 ? 1 : -1, el);
      // a tap must not follow a swipe
      if (Math.abs(dx) > SLOP) {
        const stop = (ev: Event) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        el.addEventListener('click', stop, { capture: true, once: true });
        window.setTimeout(() => el.removeEventListener('click', stop, { capture: true }), 400);
      }
    };
    // while we own a gesture the browser must not scroll along (needs a non-passive listener)
    const touchMove = (e: TouchEvent) => {
      if (g?.axis === 'x' || g?.axis === 'y') e.preventDefault();
    };
    const onUp = (e: PointerEvent) => up(e);
    const onCancel = (e: PointerEvent) => up(e, true);
    el.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    el.addEventListener('touchmove', touchMove, { passive: false });
    return () => {
      el.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      el.removeEventListener('touchmove', touchMove);
    };
  }, []);

  return ref;
}
